import { filterNotEmpty } from '../../utils';
import { reactNodeToString } from '../../utils/reactNode';
import { type GroupedInputChip, InputChip, type TreeItem } from '../Chip/types';
import { Node, NodeWithParent } from './types';
import { transverseTree } from './CheckBoxTreeUtils';

export function getNodesWithMatch(
  nodes: Node[],
  searchTerm?: string
): { nodes: NodeWithParent[]; matchedNodes: string[] } {
  const tempNodes: NodeWithParent[] = [];
  const copy = nodes.concat().map(
    (node) =>
      ({
        ...node,
        children: node.children?.length ? node.children.concat() : undefined
      }) as NodeWithParent
  );

  transverseTree(copy, [], (node: NodeWithParent) => {
    node.original_children = node.children?.concat();
  });

  // flatten nodes and add parentUid
  const currentLevel: NodeWithParent[] = [...copy];
  for (let i = 0; i < currentLevel.length; i += 1) {
    if (currentLevel?.[i]?.children?.length) {
      currentLevel.splice(
        i + 1,
        0,
        ...(currentLevel?.[i]?.children?.map((node) => {
          node.parentUid = currentLevel[i]?.value;
          return node;
        }) ?? [])
      );
    }
    const node = currentLevel[i];
    if (node) {
      tempNodes.push(node);
    }
  }
  // reverse order to iterate from child to parent
  const reversed = tempNodes.reverse();
  // if child match then add parentUid to matchedParents to marked them as matched as well
  const matchedParents: Set<string> = new Set<string>();
  reversed.forEach((node) => {
    if (
      searchTerm &&
      (reactNodeToString(node.label).toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()) ||
        matchedParents.has(node.value))
    ) {
      matchedParents.add(node.value);
      node.match = true;
      if (node.parentUid) {
        matchedParents.add(node.parentUid);
      }
    } else if (searchTerm) {
      node.match = false;
    } else {
      // if search term is empty remove information about match
      delete node.match;
    }
  });
  return {
    nodes: filterNotMatchingElements(copy),
    matchedNodes: Array.from(matchedParents)
  };
}

export const getDropdownWidth = (anchorEl: HTMLElement | null): number =>
  anchorEl ? anchorEl?.getBoundingClientRect().width : 250;

export function filterNotMatchingElements(nodes: Node[]): Node[] {
  return nodes
    .filter((node) => typeof node.match === 'undefined' || node.match)
    .map((node) =>
      !node.children?.length
        ? node
        : {
            ...node,
            children: filterNotMatchingElements(node.children)
          }
    );
}

export function getChipList(unknownLabel: string, nodes: NodeWithParent[], filter?: string[]): InputChip[] {
  const result: InputChip[] = [];
  const currentLevel: NodeWithParent[] = [...nodes];
  for (let i = 0; i < currentLevel.length; i += 1) {
    if (currentLevel?.[i]?.children?.length) {
      currentLevel.splice(i + 1, 0, ...(currentLevel?.[i]?.children ?? []));
    }
    result.push({
      label: reactNodeToString(currentLevel[i]?.label ?? unknownLabel),
      value: currentLevel[i]?.value ?? 'UNKNOWN'
    });
  }
  return result.filter((item) => filter?.includes(item.value));
}

export function getGroupedChips(chips: InputChip[], nested: TreeItem[]): GroupedInputChip[] {
  const groupedMap = groupIntoMap(nested, chips);

  return Array.from(groupedMap).map(([, values]) => toGroupedChip(values));
}

function toGroupedChip([parentChip, ...subChips]: InputChip[]): GroupedInputChip {
  const subGroupsLabels = subChips?.length
    ? `: ${subChips.map((chip) => reactNodeToString(chip.label)).join(', ')}`
    : '';
  return {
    label: `${reactNodeToString(parentChip?.label)}${subGroupsLabels}`,
    values: filterNotEmpty([parentChip?.value, ...(subChips ?? []).map((chip) => chip.value)])
  };
}

function groupIntoMap(
  tree: TreeItem[] | undefined,
  chips: InputChip[],
  selectedItems: Map<InputChip['value'], (InputChip | TreeItem)[]> = new Map<
    InputChip['value'],
    (InputChip | TreeItem)[]
  >(),
  firstSelectedItem?: InputChip['value']
) {
  const treeLength = tree?.length ?? 0;
  for (let i = 0; i < treeLength; i++) {
    const currentItem = tree?.[i];
    const selectedChip = chips.find((chip) => chip.value === currentItem?.value);
    let selected = firstSelectedItem;
    if (selectedChip) {
      if (firstSelectedItem) {
        const firstSelectedValues = selectedItems.get(firstSelectedItem) ?? [];
        const inputChips: (InputChip | TreeItem)[] = [...firstSelectedValues];
        if (currentItem) inputChips.push(currentItem);
        selectedItems.set(firstSelectedItem, inputChips);
      } else if (currentItem?.value) {
        selectedItems.set(currentItem?.value, [currentItem]);
        selected = currentItem?.value;
      }
    } // if child has children of its own, continue digging.
    if (currentItem?.children?.length) {
      groupIntoMap(tree?.[i]?.children, chips, selectedItems, selected);
    }
  }
  return selectedItems;
}
